import { useEffect, useState } from 'react';
import { getUser } from '../../services/auth';
import { useSaldoResumo } from './useSaldoResumo';
import { useAtfEmAndamentoCount } from './useAtfEmAndamento';
import { CooperadoLogo } from './CooperadoLogo';
import { BotaoFeedback } from '../feedback/BotaoFeedback';
import { InicioAba } from './abas/InicioAba';
import { EmitirAba } from './abas/EmitirAba';
import { AcompanharAba } from './abas/AcompanharAba';
import { CreditosAba } from './abas/CreditosAba';
import { FaturasAba } from './abas/FaturasAba';
import { NotaFiscalAba } from './abas/NotaFiscalAba';
import { authFetch } from '../../services/api';
import type { CooperadoAba } from './types';
import './cooperado.css';

interface CooperadoShellProps {
  onLogout: () => void;
}

const ABAS_FIXAS: Array<{ id: CooperadoAba; rotulo: string }> = [
  { id: 'inicio', rotulo: 'Início' },
  { id: 'emitir', rotulo: 'Emitir' },
  { id: 'acompanhar', rotulo: 'Acompanhar' },
];

function iniciaisDoNome(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase();
}

/**
 * Casca do painel do cooperado (spec 0034) — porta o §0 do handoff de design
 * (`.github/design/design_handoff_painel_cooperado/README.md`): header
 * sticky, logo de 3 pílulas, navegação em pílulas com badge de contagem na
 * aba Acompanhar, pílula de saldo, avatar do usuário.
 *
 * Deliberadamente FORA de AppLayout/Dashboard (DD-05 do plan.md): o perfil
 * `domCargo = 'Motorista'` nunca vê sidebar nem o Dashboard clássico, só
 * este shell. A letra é a do painel inteiro, Hanken Grotesk (ADR-0010).
 */
export function CooperadoShell({ onLogout }: CooperadoShellProps) {
  const usuario = getUser();
  const [aba, setAba] = useState<CooperadoAba>('inicio');
  const { saldo, tipoEmissao } = useSaldoResumo();
  const emAndamento = useAtfEmAndamentoCount();

  /**
   * A quarta aba depende do regime de cobrança.
   *
   * `PREPAGO` compra crédito; RECORRENTE, ESPORADICO **e tipo ausente** são
   * faturados. O nulo cai em Faturas porque é o comportamento de 195 dos 199 —
   * e uma aba de fatura vazia informa, enquanto uma de crédito vazia confunde,
   * que é exatamente o defeito corrigido aqui.
   *
   * Enquanto o tipo carrega (`undefined`), só as três fixas aparecem: a quarta
   * trocando de rótulo na frente do cooperado seria pior que aparecer depois.
   */
  const quartaAba: { id: CooperadoAba; rotulo: string } | null =
    tipoEmissao === undefined ? null
      : tipoEmissao === 'PREPAGO' ? { id: 'creditos', rotulo: 'Créditos' }
      : { id: 'faturas', rotulo: 'Faturas' };
  /**
   * A aba Nota fiscal (0113) só existe para o cooperado pessoa jurídica: a leitura
   * da ficha responde NAO_E_PJ para a pessoa física, e sem a permissão nada aparece.
   * O número é o que ele precisa levar à cooperativa: o certificado.
   */
  const [notaFiscal, setNotaFiscal] = useState<{ pedemAcao: number } | null>(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const ficha = await authFetch('/api/cooperado/nfse/ficha');
        if (!ficha.ok || !vivo) return;
        const r = await authFetch('/api/cooperado/nfse/contador');
        const corpo = await r.json().catch(() => ({}));
        if (vivo) setNotaFiscal({ pedemAcao: Number(corpo?.data?.pedemAcao) || 0 });
      } catch { /* sem a aba: o resto do portal segue */ }
    })();
    return () => { vivo = false; };
  }, []);

  const abasBase = quartaAba ? [...ABAS_FIXAS, quartaAba] : ABAS_FIXAS;
  const abas = notaFiscal ? [...abasBase, { id: 'nota-fiscal' as CooperadoAba, rotulo: 'Nota fiscal' }] : abasBase;

  // Deep-link ou estado retido não podem deixar o cooperado numa aba que já
  // não existe para ele — a tela ficaria vazia sem explicação.
  //
  // Só age sobre a aba que NÃO é a dele: comparar com `quartaAba.id` derrubava
  // também a navegação legítima, e quem mais sofria era o pré-pago vindo do
  // botão "Ver Minhas Faturas" da tela de bloqueio — ele ia para 'faturas',
  // que não é a aba dele, e voltava ao início sem entender por quê.
  const abaProibida: CooperadoAba | null =
    quartaAba?.id === 'creditos' ? 'faturas'
      : quartaAba?.id === 'faturas' ? 'creditos'
      : null;

  useEffect(() => {
    if (abaProibida && aba === abaProibida) setAba('inicio');
  }, [abaProibida, aba]);

  const nome = usuario?.nomeUsuario ?? usuario?.usuario ?? 'Cooperado';

  return (
    <div className="co-portal">
      <header className="co-topo">
        <div className="co-topo-miolo">
          <CooperadoLogo />

          <nav className="co-abas" aria-label="Seções do portal">
            {abas.map(a => (
              <button
                key={a.id}
                type="button"
                className="co-aba"
                aria-current={aba === a.id ? 'page' : undefined}
                onClick={() => setAba(a.id)}
              >
                {a.rotulo}
                {a.id === 'acompanhar' && emAndamento > 0 && (
                  <span className="co-aba-contagem" aria-label={`${emAndamento} em andamento`}>{emAndamento}</span>
                )}
                {a.id === 'nota-fiscal' && (notaFiscal?.pedemAcao ?? 0) > 0 && (
                  <span className="co-aba-contagem" aria-label="o certificado A1 pede atenção">{notaFiscal!.pedemAcao}</span>
                )}
              </button>
            ))}
          </nav>

          {saldo && (
            <button type="button" className="co-saldo" onClick={() => setAba('creditos')}>
              <span className="co-saldo-texto">
                <span className="co-rotulo">Saldo para emitir</span>
                <span className="co-saldo-valor">
                  {saldo.saldoDisponivel.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </span>
              <span className="co-saldo-acao">Recarregar</span>
            </button>
          )}

          <BotaoFeedback tela={`Portal › ${abas.find((a) => a.id === aba)?.rotulo ?? 'Início'}`} />

          <div className="co-conta">
            <span className="co-conta-iniciais" aria-hidden="true">{iniciaisDoNome(nome)}</span>
            <span className="co-conta-texto">
              <span className="co-conta-nome">{nome}</span>
              <span className="co-conta-papel">
                Cooperado{usuario?.idUsuario ? ` · ${usuario.idUsuario}` : ''}
              </span>
            </span>
            <button type="button" className="co-btn co-btn-secundario" onClick={onLogout}>
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className={aba === 'emitir' ? 'co-conteudo-cheio' : 'co-conteudo'}>
        {aba === 'inicio' && <InicioAba onNavegar={setAba} />}
        {aba === 'emitir' && <EmitirAba onNavegar={setAba} />}
        {aba === 'acompanhar' && <AcompanharAba onNavegar={setAba} />}
        {aba === 'creditos' && <CreditosAba />}
        {aba === 'faturas' && <FaturasAba />}
        {aba === 'nota-fiscal' && <NotaFiscalAba />}
      </main>
    </div>
  );
}
